import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, CalendarCheck, Utensils, Newspaper, Megaphone, TrendingUp,
  CheckCircle2, ArrowRight, Loader2, Phone, CreditCard, UserRound, Building2,
} from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (c) => (c / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: c % 100 ? 2 : 0 });

const PERKS = [
  { icon: Sparkles, title: 'Full profile management', desc: 'We build and maintain your Eventism profile — photos, story, branding, hours, everything kept fresh.' },
  { icon: CalendarCheck, title: 'Events, experiences & reservations — handled', desc: 'We create, publish, price and manage every event, experience and reservation flow for you.' },
  { icon: Megaphone, title: 'A brand-new marketing channel', desc: 'Your listings ride Eventism\'s SEO, category pages, city guides, email blasts and AI-agent discovery.' },
  { icon: Newspaper, title: 'Featured Editorial write-up', desc: 'Our editors publish a dedicated write-up about your business in Eventism\'s Editorial Guides.' },
  { icon: TrendingUp, title: 'Sales & performance reporting', desc: 'We track every ticket, booking and reservation so you always know what the platform is earning you.' },
  { icon: Utensils, title: 'Built for creators & restaurants', desc: 'Whether you throw events or run a dining room, the same team runs your presence end to end.' },
];

const STEPS = [
  { n: '01', title: 'Sign up', desc: 'Pay the $25 setup online, or request a call and we\'ll tailor a quote.' },
  { n: '02', title: 'Onboarding call', desc: 'We learn your brand, menu or event calendar, and your goals.' },
  { n: '03', title: 'We build & manage', desc: 'Profile, events, experiences and reservations go live — run by us.' },
  { n: '04', title: 'You get customers', desc: 'Sales flow in; we take a small percentage of each sale we manage.' },
];

const OFFERINGS = [
  { value: 'profile', label: 'Profile & branding', icon: Sparkles },
  { value: 'events', label: 'Events', icon: CalendarCheck },
  { value: 'experiences', label: 'Experiences', icon: TrendingUp },
  { value: 'reservations', label: 'Reservations', icon: Utensils },
  { value: 'marketing', label: 'Marketing & promotion', icon: Megaphone },
  { value: 'everything', label: 'Everything — run it all', icon: CheckCircle2 },
];

function SignupForm() {
  const [form, setForm] = useState({ name: '', business_name: '', email: '', phone: '', business_type: 'event_creator', details: '' });
  const [services, setServices] = useState([]);
  const [busy, setBusy] = useState('');
  const [leadDone, setLeadDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleService = (v) => {
    if (v === 'everything') {
      setServices(services.includes('everything') ? [] : ['everything']);
      return;
    }
    const next = services.includes(v)
      ? services.filter((s) => s !== v)
      : [...services.filter((s) => s !== 'everything'), v];
    setServices(next);
  };

  const submit = async (path) => {
    if (!form.name.trim() || !form.business_name.trim() || !form.email.trim()) {
      toast.error('Name, business name and email are required');
      return;
    }
    setBusy(path);
    try {
      const r = await axios.post(`${API}/managed/signup`, { ...form, services, path });
      if (path === 'pay_now' && r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      setLeadDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit');
      setBusy('');
    }
  };

  if (leadDone) {
    return (
      <div className="text-center py-10 space-y-3" data-testid="managed-lead-success">
        <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
        <h3 className="text-xl font-heading font-bold">You're on the list!</h3>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">Our team will reach out within one business day to build your plan and quote your setup.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="managed-signup-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input placeholder="Your name *" value={form.name} onChange={set('name')} data-testid="managed-name-input" />
        <Input placeholder="Business name *" value={form.business_name} onChange={set('business_name')} data-testid="managed-business-input" />
        <Input type="email" placeholder="Email *" value={form.email} onChange={set('email')} data-testid="managed-email-input" />
        <Input placeholder="Phone" value={form.phone} onChange={set('phone')} data-testid="managed-phone-input" />
      </div>
      <select value={form.business_type} onChange={set('business_type')}
        className="w-full h-10 px-3 border border-border rounded-md bg-background text-sm" data-testid="managed-type-select">
        <option value="event_creator">I'm an event creator</option>
        <option value="restaurant">I run a restaurant</option>
        <option value="venue">I run a venue</option>
        <option value="other">Something else</option>
      </select>
      <div className="space-y-2 pt-1">
        <p className="text-sm font-heading font-semibold">What should we manage for you?</p>
        <div className="flex flex-wrap gap-2" data-testid="managed-offerings-row">
          {OFFERINGS.map((o) => {
            const on = services.includes(o.value);
            return (
              <button key={o.value} type="button" onClick={() => toggleService(o.value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${on ? 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'border-border hover:bg-accent'}`}
                data-testid={`offering-${o.value}`}>
                <o.icon className="h-3.5 w-3.5" />{o.label}
                {on && <CheckCircle2 className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </div>
      </div>
      <Textarea rows={2} placeholder="Anything else we should know? (optional)"
        value={form.details} onChange={set('details')} data-testid="managed-details-input" />
      <div className="grid sm:grid-cols-2 gap-3 pt-1">
        <Button size="lg" onClick={() => submit('pay_now')} disabled={!!busy}
          className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 hover:text-slate-950 font-heading font-bold" data-testid="managed-pay-now-btn">
          {busy === 'pay_now' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2" />Pay $25 & get started</>}
        </Button>
        <Button size="lg" variant="outline" onClick={() => submit('talk_to_us')} disabled={!!busy}
          className="rounded-full" data-testid="managed-talk-btn">
          {busy === 'talk_to_us' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Phone className="h-4 w-4 mr-2" />Talk to us first</>}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Secure checkout via Stripe. The setup fee covers onboarding — a small percentage of each managed sale applies after launch.</p>
    </div>
  );
}

function QuotePayCard({ clientId }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/managed/clients/${clientId}/summary`).then((r) => setSummary(r.data)).catch(() => {});
  }, [clientId]);
  const pay = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/managed/clients/${clientId}/setup-checkout`);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setBusy(false);
    }
  };
  if (!summary) return null;
  return (
    <Card className="border-amber-500/40 bg-amber-500/5 max-w-lg mx-auto" data-testid="managed-quote-card">
      <CardContent className="p-6 text-center space-y-3">
        <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30">Your custom setup quote</Badge>
        <h3 className="text-xl font-heading font-bold">{summary.business_name}</h3>
        {summary.setup_paid ? (
          <p className="text-sm text-emerald-500 flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4" />Setup already paid — welcome aboard!</p>
        ) : (
          <>
            <p className="text-4xl font-display font-bold">{fmt(summary.setup_fee_cents)}</p>
            <p className="text-sm text-muted-foreground">One-time setup for Eventism Managed Services</p>
            <Button size="lg" onClick={pay} disabled={busy} className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 hover:text-slate-950 font-heading font-bold" data-testid="managed-quote-pay-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Complete setup <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function ManagedServicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const verified = useRef(false);
  const [paidOk, setPaidOk] = useState(false);
  const payClientId = searchParams.get('pay');

  useEffect(() => {
    const cid = searchParams.get('client_id');
    if (!cid || !searchParams.get('paid') || verified.current) return;
    verified.current = true;
    axios.get(`${API}/managed/clients/${cid}/setup-status`).then((r) => {
      if (r.data.setup_paid) {
        setPaidOk(true);
        toast.success('Setup complete — welcome to Managed Services! 🎉');
      } else {
        toast.message('Payment not completed — you can try again anytime.');
      }
      searchParams.delete('client_id');
      searchParams.delete('paid');
      setSearchParams(searchParams, { replace: true });
    }).catch(() => {});
  }, [searchParams, setSearchParams]);

  return (
    <div className="min-h-screen bg-background pt-16" data-testid="managed-services-page">
      <SEO
        title="Managed Services by Eventism — We Run Your Events & Reservations"
        description="Eventism Managed Services for event creators and restaurants: we manage your profile, events, experiences and reservations, market you across the platform, and feature you in our Editorial. $25 setup."
        canonical="https://eventism.com/managed"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white" data-testid="managed-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/managed-hero.jpg)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-2xl space-y-6">
            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 uppercase tracking-[0.15em] text-[11px]">
              Eventism Managed Services
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
              We run your events.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">You run your business.</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed">
              For event creators and restaurants: our team manages your entire Eventism presence — profile, events, experiences and reservations — while the platform markets you and our editors write you up.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 hover:text-slate-950 font-heading font-bold text-base px-8" asChild>
                <a href="#signup" data-testid="hero-signup-btn">Get started — $25 setup <ArrowRight className="ml-2 h-5 w-5" /></a>
              </Button>
              <Button size="lg" variant="outline" className="rounded-full border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white text-base px-8" asChild>
                <a href="#how" data-testid="hero-how-btn">How it works</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {paidOk && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex items-center gap-3" data-testid="managed-paid-banner">
            <CheckCircle2 className="h-6 w-6 text-emerald-500 shrink-0" />
            <p className="text-sm"><strong>Setup complete!</strong> Our team will reach out within one business day to kick off your onboarding call.</p>
          </div>
        </div>
      )}

      {payClientId && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <QuotePayCard clientId={payClientId} />
        </div>
      )}

      {/* Perks */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-base md:text-lg font-heading font-bold uppercase tracking-[0.2em] text-amber-500 mb-2">What you get</h2>
        <p className="text-2xl sm:text-3xl font-display font-bold mb-8 max-w-xl">A full team behind your brand, for less than a round of coffee.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PERKS.map((p) => (
            <Card key={p.title} className="card-lift" data-testid="managed-perk-card">
              <CardContent className="p-6 space-y-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500"><p.icon className="h-5 w-5" /></span>
                <p className="font-heading font-bold">{p.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing + How it works */}
      <section id="how" className="bg-muted/40 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-base md:text-lg font-heading font-bold uppercase tracking-[0.2em] text-amber-500 mb-2">Simple pricing</h2>
            <Card className="border-amber-500/30" data-testid="managed-pricing-card">
              <CardContent className="p-7 space-y-4">
                <div className="flex items-end gap-2">
                  <p className="text-5xl font-display font-bold">$25</p>
                  <p className="text-sm text-muted-foreground pb-1.5">one-time setup</p>
                </div>
                <div className="space-y-2 text-sm">
                  {['Full profile build & management', 'All events, experiences & reservations managed', 'Platform marketing + SEO + AI-agent discovery', 'Editorial write-up by our editors', 'Sales tracking & reporting'].map((f) => (
                    <p key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />{f}</p>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground border-t border-border pt-3">
                  Plus a small managed-services percentage of each ticket, experience or reservation we sell for you — billed from face value, no surprise charges to your guests.
                </p>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-5">
            <h2 className="text-base md:text-lg font-heading font-bold uppercase tracking-[0.2em] text-amber-500">How it works</h2>
            {STEPS.map((s) => (
              <div key={s.n} className="flex gap-4" data-testid={`managed-step-${s.n}`}>
                <span className="font-display font-bold text-2xl text-amber-500/60 w-10 shrink-0">{s.n}</span>
                <div>
                  <p className="font-heading font-bold">{s.title}</p>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signup */}
      <section id="signup" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 scroll-mt-24">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-display font-bold">Let us take it from here</h2>
          <p className="text-sm text-muted-foreground mt-2">Pay the $25 setup now, or tell us about your business and we'll call you with a tailored plan.</p>
        </div>
        <Card>
          <CardContent className="p-6 sm:p-8">
            <SignupForm />
          </CardContent>
        </Card>
        <div className="flex items-center justify-center gap-6 mt-6 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5" />Event creators</span>
          <span className="flex items-center gap-1.5"><Utensils className="h-3.5 w-3.5" />Restaurants</span>
          <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />Venues</span>
        </div>
      </section>
    </div>
  );
}
